import { CopyOutlined, LoadingOutlined } from '@ant-design/icons';
import { Upload as AntdUpload, message } from 'antd';
import type { RcFile } from 'antd/lib/upload';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { getTranslatedString } from '@/utils/intl';

import { splitPdfFile } from '../split-pdf';
import { LATE_TOAST_SECONDS, runSplitInTurn } from '../upload-queue';
import { askToConfirm } from './ask-to-confirm';
import { acceptPdfOrWarn, pdfDraggerProps } from './pdf-dragger';

const { Dragger } = AntdUpload;

const ICON_STYLE = { fontSize: 32, color: '#1890ff' };

type MultiWaybillDraggerProps = {
  /** One PDF split into one-page files (a single-page PDF comes back as the original file). */
  onSplit: (file: File, pages: File[]) => void;
  onSplitFailed: (file: File, err: unknown) => void;
};

/** Dropzone for a PDF that carries one waybill per page: splits it in the browser. */
export const MultiWaybillDragger = ({ onSplit, onSplitFailed }: MultiWaybillDraggerProps) => {
  // Name of the PDF being split; null when no split is queued.
  const [splittingName, setSplittingName] = useState<string | null>(null);
  const pendingSplitsRef = useRef(0);
  const mountedRef = useRef(true);
  const declineOpenQuestionRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      declineOpenQuestionRef.current?.();
    };
  }, []);

  // Static modals outlive the page: at most one question is open (splits run in turn), closed on unmount.
  const confirm = (name: string) => async (count: number) => {
    if (!mountedRef.current) {
      message.info(
        getTranslatedString('waybills.imports.split.not-asked-after-leave', { name, count }),
        LATE_TOAST_SECONDS,
      );
      return false;
    }
    const { answer, decline } = askToConfirm({
      title: getTranslatedString('waybills.imports.split.confirm.title', { count }),
      content: getTranslatedString('waybills.imports.split.confirm.content', { name, count }),
      okText: getTranslatedString('waybills.imports.split.confirm.ok'),
      cancelText: getTranslatedString('waybills.imports.split.confirm.cancel'),
    });
    declineOpenQuestionRef.current = decline;
    try {
      return await answer;
    } finally {
      declineOpenQuestionRef.current = null;
    }
  };

  const beforeUpload = async (file: RcFile) => {
    if (!acceptPdfOrWarn(file)) return AntdUpload.LIST_IGNORE;
    pendingSplitsRef.current += 1;
    // Show progress at once: the task may wait behind a split queued before a remount.
    if (pendingSplitsRef.current === 1 && mountedRef.current) setSplittingName(file.name);
    try {
      await runSplitInTurn(async () => {
        if (mountedRef.current) setSplittingName(file.name);
        let pages: File[];
        try {
          pages = await splitPdfFile(file, { confirm: confirm(file.name) });
        } catch (err) {
          // biome-ignore lint/suspicious/noConsole: the row shows a generic message, the library's reason goes to the console
          console.warn('[imports] PDF split failed', err);
          onSplitFailed(file, err);
          return;
        }
        // No pages = the operator declined the large-split confirmation.
        if (pages.length > 0) onSplit(file, pages);
      });
    } finally {
      pendingSplitsRef.current -= 1;
      if (pendingSplitsRef.current === 0 && mountedRef.current) setSplittingName(null);
    }
    return AntdUpload.LIST_IGNORE;
  };

  return (
    <Dragger {...pdfDraggerProps} beforeUpload={beforeUpload} className="multi-waybill-dragger">
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '0 20px', textAlign: 'left' }}>
        {splittingName ? (
          <LoadingOutlined aria-hidden style={ICON_STYLE} />
        ) : (
          <CopyOutlined aria-hidden style={ICON_STYLE} />
        )}
        <div style={{ minWidth: 0 }}>
          <p className="ant-upload-text">
            {splittingName ? (
              <FormattedMessage id="waybills.imports.split.splitting" values={{ name: splittingName }} />
            ) : (
              <FormattedMessage id="waybills.imports.split.title" />
            )}
          </p>
          <p className="ant-upload-hint">
            <FormattedMessage id="waybills.imports.split.hint" />
          </p>
        </div>
      </div>
    </Dragger>
  );
};
